import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { AskField } from "@/components/AskField";
import { ProtectionGauge } from "@/components/ProtectionGauge";
import { TrustBar } from "@/components/TrustBar";
import {
  Car, Home, HeartPulse, Heart, Umbrella, Shield, FileText, ChevronRight, AlertTriangle,
  RefreshCw, DollarSign, Sparkles, Plane,
} from "lucide-react";

const CAT = {
  auto: { icon: Car, color: "text-blue-500 bg-blue-50", label: "Auto" },
  home: { icon: Home, color: "text-teal-500 bg-teal-50", label: "Home" },
  renters: { icon: Home, color: "text-teal-500 bg-teal-50", label: "Renters" },
  life: { icon: Heart, color: "text-indigo-500 bg-indigo-50", label: "Life" },
  health: { icon: HeartPulse, color: "text-rose-500 bg-rose-50", label: "Health" },
  disability: { icon: Shield, color: "text-amber-500 bg-amber-50", label: "Disability" },
  umbrella: { icon: Umbrella, color: "text-purple-500 bg-purple-50", label: "Umbrella" },
  travel: { icon: Plane, color: "text-sky-500 bg-sky-50", label: "Travel" },
};
const catInfo = (c) => CAT[(c || "").toLowerCase()] || { icon: Shield, color: "text-slate-500 bg-slate-100", label: c };
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—");

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("good_morning") : hour < 18 ? t("good_afternoon") : t("good_evening");
  const firstName = (user?.name || "").split(" ")[0];

  const attention = [];
  if (data) {
    (data.reminders || []).slice(0, 3).forEach((r) => attention.push({ icon: RefreshCw, title: r.title, sub: r.due_date, to: "/reminders" }));
    (data.protection?.gaps || []).slice(0, 2).forEach((g) => attention.push({ icon: AlertTriangle, title: g.label + " gap", sub: g.reason, to: "/protection" }));
  }

  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl font-semibold text-navy">{greet}, <span className="brand-gradient-text">{firstName}</span></h1>
            <p className="mt-1 text-slate-500">{t("protection_overview")}</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-3.5 py-1.5 text-sm font-medium text-brandteal">
            <Sparkles className="h-4 w-4" /> {t("ai_badge")}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column */}
          <div className="lg:col-span-8 space-y-6">
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-5">
              <AskField compact />
            </div>

            {/* Wallet */}
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("my_wallet")}</h2>
                <button onClick={() => navigate("/wallet")} data-testid="wallet-viewall" className="text-sm font-medium text-brandblue">{t("view_all")}</button>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {(data?.policies || []).slice(0, 4).map((p) => {
                  const ci = catInfo(p.category);
                  return (
                    <div key={p.id} data-testid={`wallet-card-${p.category}`} className="rounded-2xl border border-slate-100 p-4 hover:-translate-y-0.5 transition-transform">
                      <div className="flex items-center gap-2.5">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${ci.color}`}><ci.icon className="h-4.5 w-4.5" /></span>
                        <div><p className="font-semibold text-navy leading-tight">{t("cat_" + (p.category || "").toLowerCase())}</p><p className="text-xs text-slate-400">{p.carrier}</p></div>
                        <span className="ml-auto h-2 w-2 rounded-full bg-covered" />
                      </div>
                      <div className="mt-3 space-y-1.5 text-sm">
                        <Row k={t("premium")} v={`${money(p.monthly_premium)}/mo`} />
                        <Row k={t("renewal")} v={p.renewal_date || "—"} />
                        <Row k={t("deductible")} v={money(p.deductible)} />
                      </div>
                      <button onClick={() => navigate("/wallet")} className="mt-3 flex items-center gap-1 text-sm font-medium text-brandblue">{t("view_policy")} <ChevronRight className="h-4 w-4" /></button>
                    </div>
                  );
                })}
                {(!data?.policies || data.policies.length === 0) && (
                  <div className="col-span-full text-center py-8 text-slate-400">
                    <p>{t("no_policies_yet")}</p>
                    <button onClick={() => navigate("/documents")} className="mt-2 text-brandblue font-medium">{t("upload_first_policy")}</button>
                  </div>
                )}
              </div>
            </div>

            {/* True cost of protection */}
            {data && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label={t("monthly_premiums")} value={money(data.totals.monthly_premiums)} icon={DollarSign} />
                <Stat label={t("annual_premiums")} value={money(data.totals.annual_premiums)} icon={DollarSign} />
                <Stat label={t("largest_deductible")} value={money(data.totals.largest_deductible)} icon={Shield} />
                <Stat label={t("coverage_gaps")} value={data.totals.gaps} icon={AlertTriangle} accent={data.totals.gaps > 0} />
              </div>
            )}

            <TrustBar />
          </div>

          {/* Right column */}
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("protection_score")}</h2>
              </div>
              <div className="mt-2 flex flex-col items-center">
                <ProtectionGauge score={data?.protection?.score || 0} />
                <p className="mt-3 text-center text-sm text-slate-500">
                  {data?.protection?.gaps?.length ? `${data.protection.gaps.length} ${t("areas_review")}` : t("well_protected")}
                </p>
                <button onClick={() => navigate("/protection")} data-testid="improve-score-btn"
                  className="mt-4 w-full rounded-full border border-slate-200 py-2.5 text-sm font-medium text-brandblue hover:bg-blue-50 transition-colors">{t("improve_score")}</button>
              </div>
            </div>

            <div className="rounded-3xl bg-amber-50/50 border border-amber-100 card-shadow p-6">
              <h2 className="text-lg font-semibold text-navy flex items-center gap-2">{t("needs_attention")}
                {attention.length > 0 && <span className="rounded-full bg-amber-500 px-2 text-xs text-white">{attention.length}</span>}</h2>
              <div className="mt-4 space-y-3">
                {attention.length === 0 && <p className="text-sm text-slate-500">{t("nothing_attention")} 🎉</p>}
                {attention.map((a, i) => (
                  <button key={i} onClick={() => navigate(a.to)} data-testid={`attention-${i}`}
                    className="flex w-full items-start gap-3 rounded-xl bg-white p-3 text-left hover:shadow-sm transition-shadow">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600"><a.icon className="h-4 w-4" /></span>
                    <div className="flex-1"><p className="text-sm font-medium text-navy">{a.title}</p><p className="text-xs text-slate-400 line-clamp-1">{a.sub}</p></div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("upcoming")}</h2>
                <button onClick={() => navigate("/reminders")} className="text-sm font-medium text-brandblue">{t("view_all")}</button>
              </div>
              <div className="mt-4 space-y-3">
                {(data?.reminders || []).slice(0, 4).map((r) => {
                  const d = r.due_date ? new Date(r.due_date) : null;
                  return (
                    <div key={r.id} className="flex items-center gap-3">
                      <div className="flex h-11 w-11 flex-col items-center justify-center rounded-xl bg-slate-50 text-navy">
                        <span className="text-[10px] uppercase text-slate-400">{d ? d.toLocaleString("en", { month: "short" }) : ""}</span>
                        <span className="text-sm font-semibold leading-none">{d ? d.getDate() : "—"}</span>
                      </div>
                      <p className="flex-1 text-sm font-medium text-navy line-clamp-1">{r.title}</p>
                    </div>
                  );
                })}
                {(!data?.reminders || data.reminders.length === 0) && <p className="text-sm text-slate-400">{t("no_upcoming")}</p>}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AppLayout>
  );
}

const Row = ({ k, v }) => (<div className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-medium text-navy">{v}</span></div>);
const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-4">
    <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-xs">{label}</span></div>
    <p className={`mt-1.5 font-display text-2xl font-semibold ${accent ? "text-amber-600" : "text-navy"}`}>{value}</p>
  </div>
);
